import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loaded = false;
  private loadingPromise?: Promise<void>;
  private autocompleteService: google.maps.places.AutocompleteService | null = null;

  /**
   * Load Google Maps API and initialize AutocompleteService
   * @param apiKey Optional API key, defaults to environment.googleMapsApiKey
   * @returns Promise that resolves when Google Maps API is loaded
   */
  load(apiKey?: string): Promise<void> {
    // Already loaded
    if (this.loaded) {
      return Promise.resolve();
    }

    // Already loading
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    const mapsApiKey = apiKey ?? (environment as any).googleMapsApiKey;
    if (!mapsApiKey) {
      return Promise.reject(new Error('Google Maps API key is not configured'));
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        this.loaded = true;
        // Initialize AutocompleteService after script loads
        if (typeof google !== 'undefined' && google.maps?.places) {
          this.autocompleteService = new google.maps.places.AutocompleteService();
        }
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  /**
   * Get AutocompleteService instance
   * @returns AutocompleteService or null if not loaded
   */
  getAutocompleteService(): google.maps.places.AutocompleteService | null {
    if (!this.loaded) {
      return null;
    }
    
    // Re-initialize if service is null but Google Maps is loaded
    if (!this.autocompleteService && typeof google !== 'undefined' && google.maps?.places) {
      this.autocompleteService = new google.maps.places.AutocompleteService();
    }
    
    return this.autocompleteService;
  }

  /**
   * Check if Google Maps API is loaded
   */
  isLoaded(): boolean {
    return this.loaded && typeof google !== 'undefined' && google.maps?.places !== undefined;
  }

  /**
   * Driving distance in miles from one origin to each destination (imperial).
   * Returns null for destinations that could not be calculated.
   */
  async getDrivingDistancesMiles(origin: string, destinations: string[]): Promise<(number | null)[]> {
    const trimmedOrigin = (origin ?? '').trim();
    if (!trimmedOrigin || destinations.length === 0) {
      return destinations.map(() => null);
    }

    await this.load();

    if (typeof google === 'undefined' || !google.maps?.DistanceMatrixService) {
      return destinations.map(() => null);
    }

    const service = new google.maps.DistanceMatrixService();
    const batchSize = 25;
    const results: (number | null)[] = new Array(destinations.length).fill(null);

    for (let start = 0; start < destinations.length; start += batchSize) {
      const batch = destinations.slice(start, start + batchSize);
      const batchMiles = await this.requestDistanceMatrixBatch(service, trimmedOrigin, batch);
      for (let i = 0; i < batchMiles.length; i++) {
        results[start + i] = batchMiles[i];
      }
    }

    return results;
  }

  private requestDistanceMatrixBatch(
    service: google.maps.DistanceMatrixService,
    origin: string,
    destinations: string[],
  ): Promise<(number | null)[]> {
    return new Promise((resolve) => {
      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations,
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          if (status !== google.maps.DistanceMatrixStatus.OK || !response?.rows?.[0]?.elements) {
            resolve(destinations.map(() => null));
            return;
          }

          const elements = response.rows[0].elements;
          resolve(
            destinations.map((_, index) => {
              const element = elements[index];
              if (
                !element ||
                element.status !== google.maps.DistanceMatrixElementStatus.OK ||
                element.distance?.value == null
              ) {
                return null;
              }
              const miles = element.distance.value / 1609.344;
              return Math.max(0, Math.round(miles));
            }),
          );
        },
      );
    });
  }
}
